import { analyzeDsd, analyzeDsdWithVersionForward } from "@/apis/dsd/route";
import type {
  AnalyzeDsdResponse,
  VersionForwardResponse,
} from "@/apis/dsd/types";
import { isDsdSectionNotFoundError } from "@/apis/errors";
import { DSD_SECTION_NOT_FOUND_MESSAGE } from "@/constants/dsd-upload";
import { AnimatedUploadIcon } from "@/components/dsd-view/upload-animation/animated-upload-graphic";
import { UploadStage } from "@/components/dsd-view/upload-progress-inline";
import {
  AnimatedText,
  ProgressBar,
  stages,
} from "@/components/mapping/retrieval-results/analyzing-animation";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import queries from "@/queries";
import { popModal, pushModal } from "@/stores/modal";
import { markNewReport } from "@/stores/new-reports";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { toast } from "sonner";

interface StageConfig {
  stage: UploadStage;
  startSec: number;
  endSec: number;
  startProgress: number;
  endProgress: number;
}

// analyze-dsd 실제 처리 단계별 소요 시간 기반 배분 (총 60초)
const STAGE_TIMELINE: StageConfig[] = [
  { stage: UploadStage.CHUNKING, startSec: 0, endSec: 15, startProgress: 0, endProgress: 25 },
  { stage: UploadStage.CATEGORIZING, startSec: 15, endSec: 35, startProgress: 25, endProgress: 55 },
  { stage: UploadStage.TABLE_PARSING, startSec: 35, endSec: 60, startProgress: 55, endProgress: 95 },
];

const TOTAL_DURATION_SEC = 60;
const TICK_INTERVAL_MS = 100;
const COMPLETE_ANIMATION_MS = 3000;
const COMPLETE_DISPLAY_MS = 1000;

const ProgressToast = ({ progress }: { progress: number }) =>
  createPortal(
    <div className="fixed flex w-[180px] p-5 pb-3 flex-col justify-center items-center gap-2 top-6 left-1/2 -translate-x-1/2 rounded-[10px] bg-neutral-100 shadow-lg z-[9999]">
      <div className="flex flex-col items-center gap-3 self-stretch">
        <ProgressBar progress={progress} />
        <span className="text-center text-neutral-500 self-stretch label-1-normal-medium">
          {progress}%
        </span>
      </div>
    </div>,
    document.body,
  );

const getProgressAtTime = (elapsedSec: number) => {
  if (elapsedSec >= TOTAL_DURATION_SEC) {
    return {
      stage: UploadStage.FINAL_STAGE,
      progress: 95,
      message: stages[UploadStage.FINAL_STAGE] as string,
    };
  }
  for (const config of STAGE_TIMELINE) {
    if (elapsedSec < config.endSec) {
      const ratio =
        (elapsedSec - config.startSec) / (config.endSec - config.startSec);
      const progress = Math.round(
        config.startProgress + ratio * (config.endProgress - config.startProgress),
      );
      return { stage: config.stage, progress, message: stages[config.stage] as string };
    }
  }
  return {
    stage: UploadStage.FINAL_STAGE,
    progress: 95,
    message: stages[UploadStage.FINAL_STAGE] as string,
  };
};

/**
 * 100ms 간격 선형 보간으로 부드러운 시뮬레이션 진행률 제공.
 * complete() 호출 시 남은 구간을 부드럽게 100%까지 채운 뒤 완료 콜백 실행.
 */
const useSimulatedProgress = (onAnimationDone: () => void) => {
  const startTimeRef = useRef(Date.now());
  const [state, setState] = useState(() => getProgressAtTime(0));
  const completingRef = useRef(false);
  const completeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const completeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressRef = useRef(0);

  useEffect(() => {
    const interval = setInterval(() => {
      if (completingRef.current) return;
      const elapsedSec = (Date.now() - startTimeRef.current) / 1000;
      const next = getProgressAtTime(elapsedSec);
      progressRef.current = next.progress;
      setState(next);
    }, TICK_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      if (completeIntervalRef.current) clearInterval(completeIntervalRef.current);
      if (completeTimeoutRef.current) clearTimeout(completeTimeoutRef.current);
    };
  }, []);

  const complete = useCallback(() => {
    if (completingRef.current) return;
    completingRef.current = true;
    const currentProgress = progressRef.current;
    const remaining = 100 - currentProgress;
    const steps = Math.max(1, Math.ceil(COMPLETE_ANIMATION_MS / TICK_INTERVAL_MS));
    let step = 0;
    const interval = setInterval(() => {
      step++;
      const progress = Math.round(currentProgress + (remaining * step) / steps);
      if (step >= steps) {
        setState({
          stage: UploadStage.COMPLETE,
          progress: 100,
          message: stages[UploadStage.COMPLETE] as string,
        });
        clearInterval(interval);
        completeIntervalRef.current = null;
        completeTimeoutRef.current = setTimeout(onAnimationDone, COMPLETE_DISPLAY_MS);
      } else {
        setState((prev) => ({ ...prev, progress }));
      }
    }, TICK_INTERVAL_MS);
    completeIntervalRef.current = interval;
  }, [onAnimationDone]);

  return { ...state, complete };
};

interface UploadingPhaseProps {
  file: File;
  /**
   * 최신 버전으로 업로드할 대상 보고서 id. 있으면 버전 포워드(이전 태깅 계승),
   * 없으면 새 보고서로 일반 분석.
   */
  srcWorkbookId?: string | null;
}

// ============================================
// UploadingPhase
// 업로드 확정 후 분석 진행률을 보여주는 단계. 마운트 시 업로드를 시작하고, 끝나면 업로드한
// 워크북의 매핑 화면을 새 탭으로 연다.
// ============================================

export const UploadingPhase = ({
  file,
  srcWorkbookId,
}: UploadingPhaseProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const uploadDoneRef = useRef(false);
  const cancelledRef = useRef(false);
  // 업로드가 끝나면 새 워크북 매핑 화면을 새 탭으로 연다. 버전 포워드(직전본 srcWorkbookId
  // 가 있는 업로드)면 그 탭에 "변경 사항 확인"까지 띄우므로, 상세 대사("변경 내역 전체
  // 보기", dsd-dsd)로 넘길 업로드 파일 짝(src/trgFileId)도 같이 기억한다.
  const uploadedRef = useRef<{
    workbookId: string;
    srcFileId?: string;
    trgFileId?: string;
  } | null>(null);

  // 새 워크북의 첫 카테고리를 조회해 매핑 화면을 새 탭으로 연다(useOpenReport 와 동일
  // 규칙). 버전 포워드면 vfReviewSrcWb(직전본)·reviewSrc/Trg(파일) 쿼리가 실려, 열린 탭이
  // 표별 대사를 모달로 보여준다.
  const openWorkbookTab = useCallback(
    async (workbookId: string, params: URLSearchParams) => {
      try {
        const data = await queryClient.fetchQuery(
          queries.workbook(workbookId)._ctx.categories(),
        );
        const firstCategory = [...(data?.categories ?? [])].sort((a, b) =>
          a.category.localeCompare(b.category, "ko", { numeric: true }),
        )[0];
        if (!firstCategory) return;
        const query = params.toString();
        const path = `/mapping/${workbookId}/${firstCategory.id}`;
        const url = query ? `${path}?${query}` : path;
        // 업로드 완료(비동기) 시점이라 새 탭 open 이 팝업 차단될 수 있다.
        // 차단되면(반환 null) 현재 탭에서 이동해 최소한 보고서는 열리게 한다.
        const opened = window.open(url, "_blank");
        if (!opened) navigate(url);
      } catch (error) {
        console.error("업로드한 보고서 탭 열기 실패:", error);
      }
    },
    [queryClient, navigate],
  );

  const handleComplete = useCallback(() => {
    if (cancelledRef.current) return;
    popModal();
    toast.success("DSD 보고서가 업로드되었습니다.");
    queryClient.invalidateQueries({ queryKey: queries.dsd().queryKey });
    const uploaded = uploadedRef.current;
    if (!uploaded) return;
    const params = new URLSearchParams();
    if (srcWorkbookId) {
      params.set("vfReviewSrcWb", srcWorkbookId);
      if (uploaded.srcFileId) params.set("reviewSrc", uploaded.srcFileId);
      if (uploaded.trgFileId) params.set("reviewTrg", uploaded.trgFileId);
    }
    void openWorkbookTab(uploaded.workbookId, params);
  }, [queryClient, openWorkbookTab, srcWorkbookId]);

  const { progress, message, complete } = useSimulatedProgress(handleComplete);

  const { mutate: uploadMutate } = useMutation<
    AnalyzeDsdResponse | VersionForwardResponse,
    Error,
    File
  >({
    mutationFn: (f: File) =>
      srcWorkbookId
        ? analyzeDsdWithVersionForward(f, { srcWorkbookId })
        : analyzeDsd(f, { autoAnalyzeXbrl: true }),
    onSuccess: (data) => {
      if (!uploadDoneRef.current && !cancelledRef.current) {
        uploadDoneRef.current = true;
        // 목록에서 이 보고서가 어느 연도에 생겼는지 점으로 알려주기 위해 기억해 둔다.
        markNewReport(data.workbookId);
        // 일반 분석에서 XBRL 트리거가 안 됐어도 클릭 안내는 하지 않는다 — 업로드가 끝나면
        // 매핑 화면을 자동으로 열고, 그 진입에서 useAnalyzeXbrlOnEntry 가 분석을 건다.
        uploadedRef.current = { workbookId: data.workbookId };
        // 버전 포워드(직전본 선택 업로드)면 응답에 srcFileId 키가 있다. 표별 대사는
        // 워크북 id 로 하고, 파일 id 는 있으면 "전체 보기" 버튼에만 쓴다(없어도 무방).
        if (srcWorkbookId && "srcFileId" in data) {
          uploadedRef.current = {
            workbookId: data.workbookId,
            srcFileId: data.srcFileId ?? undefined,
            trgFileId: data.trgFileId ?? undefined,
          };
        }
        complete();
      }
    },
    onError: (error: Error) => {
      if (cancelledRef.current) return;
      popModal();
      if (isDsdSectionNotFoundError(error)) {
        toast.error(DSD_SECTION_NOT_FOUND_MESSAGE);
      } else {
        toast.error("DSD 보고서 업로드에 실패했습니다.");
      }
    },
  });

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const startedRef = useRef(false);
  useEffect(() => {
    if (!startedRef.current) {
      startedRef.current = true;
      uploadMutate(file);
    }
  }, [file, uploadMutate]);

  return (
    <ModalWrapper className="w-[700px] max-w-[700px] h-[500px] flex flex-col items-center gap-4 rounded-[10px] bg-white shadow-2xl p-0 pb-8">
      <div className="w-full flex flex-row justify-start py-3 px-5 items-center gap-2 self-stretch">
        <h2 className="headline-1-semibold text-primary">DSD 파일 업로드</h2>
      </div>
      <div className="flex w-[660px] max-w-[660px] py-10 px-[197px] flex-col justify-center items-center gap-2.5 flex-1 rounded-md border-[0.67px] border-blue-300 bg-white shadow-blue-50">
        <AnimatedUploadIcon />
      </div>
      <AnimatedText message={message} />
      <ProgressToast progress={progress} />
    </ModalWrapper>
  );
};

/**
 * 업로드 진행 모달을 띄운다. 파일 선택 모달(업로드·버전 업데이트)이 자기를 닫고 이걸 부른다.
 *
 * 닫힘 차단을 이 화면 옆에 두는 이유: 업로드 중에 모달이 닫히면 요청은 계속 진행되는데
 * 완료 처리(성공 토스트·목록 갱신·보고서 새 탭 열기)가 전부 스킵돼 "업로드했는데 목록에
 * 없음"이 된다. 이 규칙이 호출처에 흩어지면 세 번째 호출처에서 빠뜨려도 아무도 못 잡는다.
 */
export const pushUploadingModal = (
  file: File,
  srcWorkbookId: string | null,
) => {
  pushModal(UploadingPhase, {
    file,
    srcWorkbookId,
    closeOnPointerDownOutside: false,
    closeOnEscapeKey: false,
  });
};
